// Shared enums, defaults, and small pure helpers used by both server and web.

export const SINGLETON_ID = 'singleton';

// ── Video quality axes ───────────────────────────────────────────────────────
// A release's quality is parsed into three independent axes. The "tier" used by
// quality profiles is the resolution+source pair (QualityKey); codec is only a
// scoring tie-break, so it stays out of the profile UI.
export const RESOLUTIONS = ['2160p', '1080p', '720p', '480p', 'sd', 'unknown'] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

export const SOURCES = ['bluray', 'webdl', 'webrip', 'hdtv', 'dvd', 'unknown'] as const;
export type Source = (typeof SOURCES)[number];

export const CODECS = ['x265', 'x264', 'unknown'] as const;
export type Codec = (typeof CODECS)[number];

/** Quality tier = `${resolution}-${source}` (plus the flat `sd`/`unknown`). */
export const QUALITY_KEYS = [
  '2160p-bluray',
  '2160p-webdl',
  '2160p-webrip',
  '1080p-bluray',
  '1080p-webdl',
  '1080p-webrip',
  '1080p-hdtv',
  '720p-bluray',
  '720p-webdl',
  '720p-webrip',
  '720p-hdtv',
  '480p-dvd',
  '480p-hdtv',
  'sd',
  'unknown',
] as const;
export type QualityKey = (typeof QUALITY_KEYS)[number];

export const QUALITY_LABELS: Record<QualityKey, string> = {
  '2160p-bluray': '2160p BluRay',
  '2160p-webdl': '2160p WEB-DL',
  '2160p-webrip': '2160p WEBRip',
  '1080p-bluray': '1080p BluRay',
  '1080p-webdl': '1080p WEB-DL',
  '1080p-webrip': '1080p WEBRip',
  '1080p-hdtv': '1080p HDTV',
  '720p-bluray': '720p BluRay',
  '720p-webdl': '720p WEB-DL',
  '720p-webrip': '720p WEBRip',
  '720p-hdtv': '720p HDTV',
  '480p-dvd': '480p DVD',
  '480p-hdtv': '480p HDTV',
  sd: 'SD',
  unknown: 'Unknown',
};

/** Higher = better. Resolution dominates, then source, then codec. */
export const RES_RANK: Record<Resolution, number> = {
  '2160p': 4000,
  '1080p': 3000,
  '720p': 2000,
  '480p': 1000,
  sd: 500,
  unknown: 0,
};
export const SRC_RANK: Record<Source, number> = {
  bluray: 400,
  webdl: 300,
  webrip: 200,
  hdtv: 100,
  dvd: 80,
  unknown: 0,
};
export const CODEC_RANK: Record<Codec, number> = { x265: 20, x264: 10, unknown: 0 };

// ── Release language (parsed from scene tags) ────────────────────────────────
export const LANGUAGES = ['german', 'english', 'multi', 'french', 'spanish', 'italian', 'dutch'] as const;
export type Language = (typeof LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<Language, string> = {
  german: 'German',
  english: 'English',
  multi: 'Multi / Dual',
  french: 'French',
  spanish: 'Spanish',
  italian: 'Italian',
  dutch: 'Dutch',
};

// What the user can set as the preferred language in Settings ('any' = no preference).
export const PREFERRED_LANGUAGE_OPTIONS = [
  'german',
  'english',
  'french',
  'spanish',
  'italian',
  'dutch',
  'any',
] as const;
export type PreferredLanguage = (typeof PREFERRED_LANGUAGE_OPTIONS)[number];

// ── Newznab categories (standard scheme used by scenenzbs) ───────────────────
export const NEWZNAB_CATEGORIES = {
  movies: 2000,
  moviesSD: 2030,
  moviesHD: 2040,
  moviesUHD: 2045,
  tv: 5000,
  tvSD: 5030,
  tvHD: 5040,
  tvUHD: 5045,
  tvAnime: 5070,
} as const;

export const DEFAULT_MOVIE_CATEGORIES = '2000,2030,2040,2045';
export const DEFAULT_TV_CATEGORIES = '5000,5030,5040,5045,5070';
/** Everything we ever search — used as the Settings default + manual search. */
export const DEFAULT_CATEGORIES = '2000,2030,2040,2045,5000,5030,5040,5045,5070';

// ── Media lifecycle state machine (shared by movie / season / episode) ───────
export const MEDIA_STATES = [
  'unmonitored',
  'wanted',
  'searching',
  'grabbed',
  'downloading',
  'importing',
  'imported',
  'failed',
] as const;
export type MediaState = (typeof MEDIA_STATES)[number];

export const MEDIA_STATE_LABELS: Record<MediaState, string> = {
  unmonitored: 'Unmonitored',
  wanted: 'Wanted',
  searching: 'Searching',
  grabbed: 'Grabbed',
  downloading: 'Downloading',
  importing: 'Importing',
  imported: 'Imported',
  failed: 'Failed',
};

// ── Download (grab) lifecycle ────────────────────────────────────────────────
export const GRAB_STATUSES = [
  'queued',
  'downloading',
  'postprocessing',
  'downloaded',
  'importing',
  'imported',
  'failed',
  'cancelled',
] as const;
export type GrabStatus = (typeof GRAB_STATUSES)[number];

/** Grab states that are still "in flight" (used by the download monitor + detail view). */
export const ACTIVE_GRAB_STATUSES: GrabStatus[] = [
  'queued',
  'downloading',
  'postprocessing',
  'downloaded',
  'importing',
];

/**
 * Grab states that occupy a concurrent-DOWNLOAD slot. `downloaded`/`importing` are past
 * the download stage, so they must NOT gate new searches — otherwise a slow or blocked
 * import wedges the whole search pipeline.
 */
export const DOWNLOAD_SLOT_STATUSES: GrabStatus[] = ['queued', 'downloading', 'postprocessing'];

// ── Search/grab target discriminator ─────────────────────────────────────────
// A release/grab targets exactly one of: a movie, a whole season (pack), or a
// single episode. (Series itself is never grabbed directly.)
export const TARGET_TYPES = ['movie', 'season', 'episode'] as const;
export type TargetType = (typeof TARGET_TYPES)[number];

/** Top-level library entities the user adds + monitors. */
export const MEDIA_KINDS = ['movie', 'series'] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

export const SERIES_TYPES = ['standard', 'anime', 'daily'] as const;
export type SeriesType = (typeof SERIES_TYPES)[number];

// ── Per-item import status (movie / episode) ─────────────────────────────────
export const IMPORT_STATUSES = ['pending', 'imported', 'skipped', 'failed'] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

// ── How completed files are placed into the output (Jellyfin) library ────────
export const IMPORT_MODES = ['hardlink', 'copy'] as const;
export type ImportMode = (typeof IMPORT_MODES)[number];

// ── How finished files are pushed to the NAS (Library Sync tab) ──────────────
export const NAS_MODES = ['move', 'copy'] as const;
export type NasMode = (typeof NAS_MODES)[number];

// ── History event types ──────────────────────────────────────────────────────
export const EVENT_TYPES = [
  'search',
  'grab',
  'download_complete',
  'download_failed',
  'import',
  'import_failed',
  'upgrade',
  'indexer_limit',
  'downloader_paused',
  'downloader_resumed',
  'error',
  'info',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

// ── User roles ───────────────────────────────────────────────────────────────
// admin: full access incl. Settings + user management. user: app access only.
export const ROLES = ['admin', 'user'] as const;
export type Role = (typeof ROLES)[number];

// ── Video files we import ────────────────────────────────────────────────────
export const VIDEO_EXTENSIONS = [
  '.mkv',
  '.mp4',
  '.avi',
  '.m4v',
  '.ts',
  '.mov',
  '.wmv',
  '.flv',
] as const;

// Smallest file we treat as a real feature/episode (skips samples/extras).
export const MIN_VIDEO_BYTES = 50 * 1024 * 1024;
